import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { HashRouter } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'

// 引入ResizeObserver polyfill，处理DOM重排
import 'resize-observer-polyfill'

import App from './App'
import { store, persistor } from '@store'
import './styles/globals.css'
// Univer 主题令牌必须进主包 CSS（index-*.css，随 index.html <link> 必然加载）：
// 2026-10-11 真机 ArkWeb 上懒加载 chunk 的令牌曾整体失效（浮层半透明/文字混叠），
// 主包路径在真机被证明可靠；此处与 SheetNoteEditor.tsx 内的引入形成双保险（vite 自动去重）。
import './styles/univer-theme-tokens.css'
import { BatchDeleteProvider } from '@contexts/BatchDeleteContext'
import { attachmentService } from '@services/AttachmentService'
import { saveFile, saveOssAttachment, isMobilePlatform, isOssKey } from './utils/fileSave'
import { setupMobileSafeArea } from './utils/mobile'
import { toast, Toaster } from '@components/ui/toast'

// [Startup] 埋点：JS 入口开始执行（模块体在所有静态 import 求值后运行）
console.log('[Startup] JS 入口开始执行:', Math.round(performance.now()), 'ms')

// DOM 就绪后立即设置安全区域（不依赖 React 渲染时序）
// 确保 CSS 变量 --app-safe-area-top 在 Header 首次渲染前就已设置
if (typeof window !== 'undefined') {
  setupMobileSafeArea()
}

// 集合预热：同步读 localStorage 中的 userId，在 React 渲染 + restoreSession 之前
// 提前发起 SQLite 持久化加载 + hydration + Electric 订阅（fire-and-forget，不阻塞启动）
// 与 restoreSession 并行可节省 0.5-1 秒；userId 缺失（未登录/已登出）时走原串行路径
const preheatUserId = ((): string | null => {
  try {
    const direct = localStorage.getItem('modern_notes_user_id')
    if (direct) return direct
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed?.state?.user?.id || null
    }
  } catch {
    // localStorage 解析失败，跳过预热
  }
  return null
})()

if (preheatUserId) {
  console.log('[Startup] 集合预热发起:', Math.round(performance.now()), 'ms')
  import('./store/collections')
    .then(m => m.initializeCollections(preheatUserId))
    .catch(e => console.warn('[main] 集合预热失败（不阻塞启动，useElectricSync 将接管重试）:', e))
}

// 视图 chunk 预热：与数据预热并行，消除"ProtectedRoute 放行 + 路由匹配后才开始加载编辑器 chunk"的串行间隙
// import() specifier 与 App.tsx 的 lazy() 完全一致，命中 ES 模块缓存，React.lazy 不会重复拉取
const preheatEditorMode = ((): string | null => {
  try { return localStorage.getItem('last_editor_mode') } catch { return null }
})()
if (preheatEditorMode === 'crdt') {
  import('@components/Pages/CRDTMultiNoteEditorPage').catch(() => {})
} else {
  // 移动端默认与兜底路由均为 /suji
  import('@components/Pages/SujiCRDTMultiNoteEditorPage').catch(() => {})
}

document.addEventListener('click', async (e) => {
  const downloadTarget = (e.target as HTMLElement).closest('[data-attachment-download]') as HTMLElement | null
  if (!downloadTarget) return

  e.preventDefault()
  e.stopPropagation()

  const url = downloadTarget.getAttribute('data-url')
  const fileName = downloadTarget.closest('[data-attachment]')?.getAttribute('data-file-name')

  if (!url || !fileName) return

  try {
    const ossKeyDetected = isOssKey(url)
    let result: Awaited<ReturnType<typeof saveFile>>

    if (ossKeyDetected && isMobilePlatform()) {
      // ★ 移动端：走 Rust 服务端 OSS 下载（绕过浏览器 ali-oss 的 CORS 限制），直接保存到下载目录
      result = await saveOssAttachment(url, fileName)
    } else {
      let fileSource: string | Blob = url
      if (ossKeyDetected) {
        const blob = await attachmentService.downloadAttachment(url)
        if (blob) fileSource = blob
        else throw new Error('从 OSS 下载附件失败')
      }
      result = await saveFile(fileSource, fileName)
    }
    if (result.success) {
      if (result.path) {
        toast.success(`附件已保存到: ${result.path}`)
      } else {
        toast.success('附件下载成功')
      }
    }
  } catch (error) {
    console.error('[全局附件下载] 下载失败:', error)
    toast.error('下载附件失败')
  }
})

// Initialize the application
const container = document.getElementById('root')

if (!container) {
  throw new Error('Root element not found')
}

const root = createRoot(container)

// 将 Redux store 暴露到 window 对象，供 cloudSyncApi 使用
if (typeof window !== 'undefined') {
  (window as any).__REDUX_STORE__ = store
}

// 隐藏 loading 屏幕的函数
const hideLoadingScreen = () => {
  if (typeof window !== 'undefined' && (window as any).hideAppLoading) {
    (window as any).hideAppLoading()
  }
}

// 订阅 persistor 状态变化
persistor.subscribe(() => {
  const { bootstrapped } = persistor.getState()
  if (bootstrapped) {
    console.log('[Startup] redux-persist bootstrapped:', Math.round(performance.now()), 'ms')
    hideLoadingScreen()
  }
})

// 生产环境禁用 StrictMode，避免双渲染问题
const isDev = import.meta.env.DEV
const AppWrapper = isDev ? React.StrictMode : React.Fragment

console.log('[Startup] React root.render 调用:', Math.round(performance.now()), 'ms')

root.render(
  <AppWrapper>
    <Provider store={store}>
      <PersistGate
        loading={null}
        persistor={persistor}
      >
        <HashRouter>
          <BatchDeleteProvider>
            <App />
            <Toaster />
          </BatchDeleteProvider>
        </HashRouter>
      </PersistGate>
    </Provider>
  </AppWrapper>
)
